import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import {
  LessonPlanOrder,
  updateLessonPlansOrder,
} from '@/data/services/lessonPlanServices';
import LessonPlan from '@/models/LessonPlan';
import LessonPlanItem from '@/models/LessonPlanItem';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { reorder } from '@/utils/list';
import alert from '@/utils/shared/alert';
import { reorderGroupedItems } from '@/utils/shared/reorder';

type UseReorderLessonPlansProps = {
  itemsCache: ListCacheManager<LessonPlanItem>;
  lessonPlanCache: ListCacheManager<LessonPlan>;
};

const useReorderLessonPlans = ({
  lessonPlanCache,
  itemsCache,
}: UseReorderLessonPlansProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.lessonPlansTab',
  });

  const reorderMutation = useMutation({
    onMutate: (order: LessonPlanOrder[]) => {
      lessonPlanCache.set(
        reorder(
          lessonPlanCache.get() ?? [],
          order.map(lessonPlan => lessonPlan.id),
        ),
      );

      const reordenedItems = reorderGroupedItems(
        itemsCache.get() ?? [],
        order.map(lessonPlan => ({
          childIds: lessonPlan.itemsIds.map(item => item.id),
          patch: { lessonPlanId: lessonPlan.id },
        })),
      );

      itemsCache.set(reordenedItems);
    },
    mutationFn: (order: LessonPlanOrder[]) => updateLessonPlansOrder(order),
    onSuccess: () => alert.success(t('reorderSuccess')),
    onError: err => {
      itemsCache.undo();
      lessonPlanCache.undo();
      alert.error(getErrorMessage(err));
    },
  });

  return {
    reorderLessonPlans: reorderMutation.mutate,
    isReordering: reorderMutation.isPending,
  };
};

export default useReorderLessonPlans;
